import React from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Col, Drawer, Form, Image, Input, InputNumber, Radio, Row, Select, Tabs, Upload } from "antd";
import { Button } from "src/components/actions/button";
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg";
import { TranslatableInput } from "src/components/Translatable";
import { useImage } from "src/hooks/use-image";
import { useUploadFile } from "src/hooks/use-upload-file";
import { useAppData } from "src/store/app-data";
import { getFormattedLangData, getInitialValues } from "src/utils/helpers";
import { API_BASE_MEDIA_URL } from "src/config";
import styled from "styled-components";

export function EditDrawer({
                             title,
                             initialValues,
                             onClose,
                             onSave,
                             cityOptions,
                             categoryOptions,
                           }) {
  const layoutConfig = useAppData((state) => state.layoutConfig);

  // Категории приезжают из CRM, и синк затрёт всё, что тут наредактируют, — поэтому поля read-only.
  // Исключение — «Любимое и новое»: она заведена нами, в CRM её нет, синка у неё нет,
  // и править её название с тумблером можно только отсюда.
  const isFavorites = Boolean(initialValues?.isFavorites);
  const editDisabled = !isFavorites;

  // Поля, которых у нашей категории нет по определению: guid-заглушка, пустой nmitMgrpID
  // (её нет в тилипаде) и пустые точки — состав считается по истории покупок, а не по pivot.
  // Оставлять на них required нельзя: поле disabled, заполнить его никак, и сохранение
  // молча падает на валидации.
  const crmFieldRequired = !isFavorites;

  const [form] = Form.useForm();

  const [fileList, setFileList] = React.useState([]);
  const { onPreview } = useImage();

  const crmImageUrl = initialValues?.crmImageUrl ?? null;
  const hasOwnImage = fileList.length > 0;

  React.useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(getInitialValues(initialValues));

      const file = initialValues.file;
      if (file) {
        setFileList([
          {
            uid: String(file.id),
            name: file.originalName,
            status: "done",
            url: `${API_BASE_MEDIA_URL}${file.path}/${file.systemName}`,
          },
        ]);
        form.setFieldsValue({ fileId: file.id });
      } else {
        setFileList([]);
        form.setFieldsValue({ fileId: null });
      }
    }
  }, [initialValues]);

  const handleBeforeUpload = async (file) => {
    const uploadedFile = await useUploadFile(file);

    if (uploadedFile?.id) {
      setFileList([
        {
          uid: String(uploadedFile.id),
          name: file.name,
          status: "done",
          url: `${API_BASE_MEDIA_URL}${uploadedFile.path}/${uploadedFile.systemName}`,
        },
      ]);

      form.setFieldsValue({ fileId: uploadedFile.id });
    }

    return false;
  };

  function prepareData(values) {
    const id = initialValues?.id;

    const data = {
      ...(id && { id, _method: "PUT" }),
      isActive: values.isActive,
      isShowToMain: values.isShowToMain,
      externalGuid: values.externalGuid,
      nmitMgrpID: values.nmitMgrpId,
      sortWeight: values.sortWeight ?? 1,
      cellCount: values.cellCount ?? 1,
      name: values.name,
      image: values.image,
      // Своя картинка живёт отдельно от image: image переписывает синк с CRM, fileId — нет.
      // null здесь осмыслен — так картинку снимают и категория возвращается на image из CRM.
      fileId: values.fileId ?? null,
      pointIds: values.pointsIds,
      groupProductCategoryIds: values.groupProductCategoryIds,
    };

    return data;
  }

  async function onSubmit(values) {
    const baseData = prepareData(values);

    const data = getFormattedLangData(baseData);

    await onSave(data, data.id);
    onClose();
    form.resetFields();
  }

  function handleClose() {
    onClose();
    form.resetFields();
  }

  const id = initialValues?.id ?? "new";

  return (
      <DrawerEdit
          width={layoutConfig.isMobile ? "90%" : "80%"}
          title={title}
          onClose={handleClose}
          open
      >
        <FormEdit form={form} layout={"vertical"} onFinish={(values) => onSubmit(values)}>
          <StyledFormItem
              key={id + "_isActive"}
              label={"Включен"}
              name={"isActive"}
              initialValue={form.isActive || true}
              rules={[
                {
                  required: true,
                  message: "Выберите вариант!",
                },
              ]}
          >
            <Radio.Group disabled={editDisabled} buttonStyle="solid" value={form.isActive || true}>
              <Radio.Button value={true}>Да</Radio.Button>
              <Radio.Button value={false}>Нет</Radio.Button>
            </Radio.Group>
          </StyledFormItem>

          <StyledFormItem
              key={id + "_isShowToMain"}
              label={"Показывать на главной"}
              name={"isShowToMain"}
              initialValue={form.isShowToMain || true}
              rules={[
                {
                  required: true,
                  message: "Выберите вариант!",
                },
              ]}
          >
            <Radio.Group buttonStyle="solid" value={form.isShowToMain ? form.isShowToMain : true}>
              <Radio.Button value={true}>Да</Radio.Button>
              <Radio.Button value={false}>Нет</Radio.Button>
            </Radio.Group>
          </StyledFormItem>

          <StyledFormItem
              label={"Название"}
              key={id + "_name"}
              rules={[
                {
                  required: true,
                  message: "Введите название!",
                },
              ]}
          >
            <TranslatableInput disabled={editDisabled}  name={"name"} id={id}></TranslatableInput>
          </StyledFormItem>

          {/* Картинку можно менять у любой категории, включая приезжающие из CRM:
              она хранится отдельным файлом, и синк её не трогает.

              Картинку из CRM показываем рядом и всегда: без неё у категории с картинкой в списке
              форма выглядела бы пустой, будто картинку потеряли. */}
          {crmImageUrl && (
            <StyledFormItem key={id + "_crmImage"} label={"Картинка из CRM"}>
              <CrmImageBlock>
                <Image src={crmImageUrl} width={104} height={104} style={{ objectFit: "contain" }} />
                <CrmImageHint>
                  {hasOwnImage
                    ? "Сейчас её перекрывает своя картинка — она и уходит в приложение. Удалите свою, чтобы вернуться к этой."
                    : "Приезжает из CRM и обновляется синхронизацией. Загрузите свою ниже, чтобы заменить её."}
                </CrmImageHint>
              </CrmImageBlock>
            </StyledFormItem>
          )}

          <StyledFormItem
              key={id + "_file"}
              label={crmImageUrl ? "Своя картинка (перекрывает картинку из CRM)" : "Картинка категории"}
          >
            <Upload
                listType="picture-card"
                maxCount={1}
                accept="image/*"
                beforeUpload={handleBeforeUpload}
                fileList={fileList}
                onPreview={onPreview}
                onRemove={() => {
                  setFileList([]);
                  form.setFieldsValue({ fileId: null });
                }}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Загрузить изображение</div>
              </div>
            </Upload>
          </StyledFormItem>

          <StyledFormItem key={id + "_fileId"} name={"fileId"} noStyle>
            <Input type="hidden" />
          </StyledFormItem>

          <StyledFormItem
              key={id + "_pointsIds"}
              label={"Точки (несколько)"}
              name={"pointsIds"}
              rules={[
                {
                  required: crmFieldRequired,
                  message: "Выберите точки!",
                },
              ]}
          >
            <Select disabled mode="multiple" options={cityOptions} />
          </StyledFormItem>

          <StyledFormItem
              key={id + "_groupProductCategoryIds"}
              label={"Группа категорий"}
              name={"groupProductCategoryIds"}
              rules={[
                {
                  required: true,
                  message: "Выберите группу!",
                },
              ]}
          >
            {/* Группа остаётся read-only и для «Любимого»: выдача каталога фильтрует
                группы, а не категории, и пустой список групп выкинет её из каталога совсем. */}
            <Select disabled mode="multiple" options={categoryOptions} />
          </StyledFormItem>

          <Row gutter={28}>
            <Col className="gutter-row" span={12}>
              <StyledFormItem
                  key={id + "_sortWeight"}
                  label={"Позиция в списке"}
                  name={"sortWeight"}
                  rules={[
                    {
                      required: true,
                      message: "Введите позицию!",
                    },
                  ]}
              >
                <InputNumber
                    value={form.sortWeight}
                    min={1}
                    style={{
                      width: "100%",
                    }}
                    placeholder={"Позиция"}
                />
              </StyledFormItem>
            </Col>

            <Col className="gutter-row" span={12}>
              <StyledFormItem
                  key={id + "_cellCount"}
                  label={"Размер ячейки"}
                  name={"cellCount"}
                  rules={[
                    {
                      required: true,
                      message: "Введите номер ячейки!",
                    },
                  ]}
              >
                <InputNumber
                    value={form.cellCount}
                    min={1}
                    style={{
                      width: "100%",
                    }}
                    placeholder={"Номер ячейки"}
                />
              </StyledFormItem>
            </Col>

            <Col className="gutter-row" span={12}>
              <StyledFormItem
                  label={"externalGuid"}
                  key={id + "_externalGuid"}
                  name={"externalGuid"}
                  rules={[
                    {
                      required: true,
                      message: "Введите externalGuid!",
                    },
                  ]}
              >
                <Input disabled />
              </StyledFormItem>
            </Col>

            <Col className="gutter-row" span={12}>
              <StyledFormItem
                  label={"nmitMgrpID"}
                  key={id + "_nmitMgrpID"}
                  name={"nmitMgrpId"}
                  rules={[
                    {
                      required: crmFieldRequired,
                      message: "Введите nmitMgrpID!",
                    },
                  ]}
              >
                <Input disabled />
              </StyledFormItem>
            </Col>
          </Row>

          <ButtonRow>
            <ButtonCol>
              <Button
                  danger
                  onClick={() => {
                    if (initialValues) {
                      form.setFieldsValue(getInitialValues(initialValues));
                      return;
                    }
                    form.resetFields();
                  }}
                  icon={<RotateClockwiseSvg />}
              >
                Вернуть
              </Button>
            </ButtonCol>
            <ButtonCol>
              <Button onClick={handleClose} icon={<CloseSvg />}>
                Закрыть
              </Button>
            </ButtonCol>
            <ButtonCol>
              <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                Сохранить
              </Button>
            </ButtonCol>
          </ButtonRow>
        </FormEdit>
      </DrawerEdit>
  );
}

const DrawerEdit = styled(Drawer)``;

const FormEdit = styled(Form)``;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`;

const CrmImageBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CrmImageHint = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  line-height: 1.4;
  max-width: 420px;
`;

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`;

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`;
